import { useFlyOutProductsBasedOnSelectedCloud } from '../../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  AutoComplete,
  DatePickerV2,
  PlanType,
  RadioButtonsV2,
  RadioButtonType,
} from '@labrav/react-components'
import { FormikProps } from 'formik'
import React, { useMemo } from 'react'
import { Typography } from '@mui/material'
import { useStyles } from '../DownloadMeteringHistory.styles'
import { labraUIDateFormat } from '@labrav/react-components'
import { Options } from '@labrav/react-components/lib/@types/v2/components/AutoComplete'
import { getPreviousMonthDate } from '@labrav/common'
import { sortProductsAlphabetically } from '../../CreateMetering/modals/CreateMeteringForm/CreateMeteringForm'
import { CloudAccountChangeV2, CloudAccountChangeParams } from '@labrav/common'
import {
  useCloudAccounts,
  getSelectedAccountIdWithCloud,
  extractAccountIdFromCombinedId,
} from '@labrav/common'
import { useAccountId } from '@labrav/common'
import { useParams, useNavigate } from 'react-router-dom'
import { useCurrentCloudProvider } from '@labrav/common'
import { useDispatch, useSelector } from '../../../../store/hooks'
import type { RootState } from '../../../../store/types'
import { setSelectedAccount } from '@labrav/common'
import { CloudType } from '@labrav/common'
import { setCloudMarketplaceProductListing } from '../../../../modules/productListingFilter/action'
import {
  setSelectedProductOnListing,
  getProducts,
} from '../../../../modules/productsListing/action'
import { actionTypeWrapper } from '@labrav/common'
import { CloudMarketplace } from '../../../../modules/productsListing/types'
import { UsageData } from '../../../../modules/usageMetering/types'

export type DownloadDetails = {
  cloudProvider: string
  timeFrame: string
  fromDate?: string
  toDate?: string
  cloudProductId?: string
  productCode?: string
}

interface DownloadMeteringHistoryModalContentProps {
  formikProps: FormikProps<DownloadDetails>
}
export const DownloadMeteringHistoryModalContent: React.FC<
  DownloadMeteringHistoryModalContentProps
> = ({ formikProps }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { classes } = useStyles()
  const { cloud: routeCloud, accountId: accountIdFromParams } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const selectedAccountIdFromRedux = useSelector<string | undefined>(
    (state: RootState) => {
      const selectedAccountIds = state.accountSelection?.selectedAccountIds as
        | Record<string, Partial<Record<CloudType, string>>>
        | undefined
      const flyoutSelections =
        selectedAccountIds?.['FLYOUT'] || selectedAccountIds?.['flyout']
      if (flyoutSelections) {
        const clouds = Object.keys(flyoutSelections)
        if (clouds.length > 0) {
          return flyoutSelections[clouds[0] as CloudType] || ''
        }
      }
      return undefined
    }
  )

  const accountId =
    selectedAccountIdFromRedux || accountIdFromParams || accountIdFromHook || ''

  const cloudMarketplace: CloudMarketplace = routeCloud
    ? (routeCloud.toUpperCase() as CloudMarketplace)
    : 'AWS'

  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')

  const marketplaceCloudAccounts = useMemo(
    () =>
      baseMarketplaceCloudAccounts.map(account => ({
        ...account,
        disabled: account.provider === 'GCP',
      })),
    [baseMarketplaceCloudAccounts]
  )

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing || cloudMarketplace
  )

  const handleCloudAccountChange = async (params: CloudAccountChangeParams) => {
    const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
    const { cloud } = params

    formikProps.setFieldValue('cloudProvider', cloud)
    // Clear selected product in form when account changes
    formikProps.setFieldValue('cloudProductId', '')
    formikProps.setFieldValue('productCode', '')

    const cloudPath = cloud.toLowerCase()
    const pathname = `/flyout/${cloudPath}/${originalAccountId}/usage-metering`
    navigate(pathname, { replace: true })

    await dispatch(
      setSelectedAccount(originalAccountId, 'flyout', cloud as CloudType)
    )

    await dispatch(setCloudMarketplaceProductListing(cloud))

    await dispatch(
      actionTypeWrapper(
        selectedCloudTypeProductListing,
        setSelectedProductOnListing(undefined)
      )
    )

    await dispatch(getProducts(originalAccountId, cloud))
  }

  const timeFrameRadioButtonMapping = [
    {
      key: '1',
      label: 'Last calendar month',
      type: RadioButtonType.STANDARD,
    },
    {
      key: '6',
      label: 'Last 6 calendar months',
      type: RadioButtonType.STANDARD,
    },
    {
      key: 'custom',
      label: 'Custom',
      type: RadioButtonType.STANDARD,
    },
  ]

  //Product listing configs
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const usageRows = useSelector((state: RootState) => state.usageData.data)
  const cloudForProducts =
    (formikProps.values.cloudProvider as CloudMarketplace) || cloudMarketplace

  const updatedProductOptions: Options[] = useMemo(() => {
    return products
      .filter(product => {
        if (!product.productName) {
          return false
        }
        if (cloudForProducts === 'AWS') {
          return (
            [PlanType.SAAS_CONTRACT, PlanType.SAAS_SUBSCRIPTION].includes(
              product.productType as PlanType
            ) &&
            (product.productType !== PlanType.SAAS_CONTRACT ||
              (product.productDimensions &&
                product.productDimensions.some(
                  dimension => dimension.isAdditionalUsageAllowed
                )))
          )
        }
        return true
      })
      .map(product => ({
        label: product.productName,
        key:
          cloudForProducts === 'AWS'
            ? (product.awsProductId ??
              product.cloudMarketplaceProductId ??
              product.productId ??
              '')
            : (product.cloudMarketplaceProductId ?? product.productId ?? ''),
      }))
  }, [products, cloudForProducts])
  const productOptions = sortProductsAlphabetically(updatedProductOptions)

  const setTimeFrame = (timeFrame: string) => {
    formikProps.setFieldValue('timeFrame', timeFrame)
    if (timeFrame === '1') {
      const { fromDate, toDate } = getPreviousMonthDate('1')
      formikProps.setFieldValue('fromDate', fromDate)
      formikProps.setFieldValue('toDate', toDate)
    } else if (timeFrame === '6') {
      const { fromDate, toDate } = getPreviousMonthDate('6')
      formikProps.setFieldValue('fromDate', fromDate)
      formikProps.setFieldValue('toDate', toDate)
    } else if (timeFrame === 'custom') {
      formikProps.setFieldValue('fromDate', '')
      formikProps.setFieldValue('toDate', '')
    }
    if (timeFrame === '') {
      setTimeFrame('1')
    }
  }

  return (
    <div className={classes.formContent}>
      <div className={classes.selectorRow}>
        <div className={classes.selectRowFields}>
          <CloudAccountChangeV2
            cloudAccounts={marketplaceCloudAccounts}
            selectedAccountId={selectedAccountIdWithCloud}
            onChange={handleCloudAccountChange}
            width={220}
            size="medium"
          />
        </div>
        <div className={classes.selectRowFields}>
          <AutoComplete
            id={'cloudProductId'}
            label={'Select product'}
            options={productOptions}
            value={
              productOptions.find(
                option => option.key === formikProps.values.cloudProductId
              ) || null
            }
            onChange={(event, newValue) => {
              const selectedOption =
                typeof newValue === 'object'
                  ? newValue
                  : productOptions.find(opt => opt.label === newValue) || null
              const key = selectedOption?.key || ''
              const codeFromUsage =
                cloudForProducts === 'AZURE'
                  ? usageRows.find(
                      (r: UsageData) => r.productDetails?.cloudProductId === key
                    )?.productDetails?.cloudProductCode
                  : undefined
              formikProps.setValues({
                ...formikProps.values,
                cloudProductId: key,
                productCode:
                  cloudForProducts === 'AZURE' ? (codeFromUsage ?? '') : '',
              })
            }}
            onBlur={formikProps.handleBlur}
            error={
              formikProps.touched.cloudProductId &&
              Boolean(formikProps.errors.cloudProductId)
            }
            helperText={
              formikProps.touched.cloudProductId &&
              (formikProps.errors.cloudProductId as string)
            }
          />
        </div>
      </div>
      <div className={classes.timeFrameSection}>
        <Typography className={classes.header}>
          Choose a time frame for the metering history
        </Typography>
        <Typography className={classes.description}>
          Start dates and end dates are in UTC.
        </Typography>
        <div className={classes.radioButtonsContainer}>
          <RadioButtonsV2
            checkedValue={{
              key: formikProps.values.timeFrame,
              value: formikProps.values.timeFrame,
            }}
            name="timeFrame"
            radioButtonMappings={timeFrameRadioButtonMapping}
            handleChange={e => {
              setTimeFrame(e.target.value)
            }}
            gapBetween="10px"
          />
          {formikProps.values.timeFrame === 'custom' && (
            <div className={classes.customDateRange}>
              <div className={classes.datePickerContainer}>
                <DatePickerV2
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  format={labraUIDateFormat}
                  field={{
                    id: 'fromDate',
                    title: 'Start date',
                    humanReadableKey: 'Start date',
                    placeHolder: 'Start date',
                    isRequired: true,
                    maxDate: 'today',
                  }}
                  views={['year', 'month', 'day']}
                />
              </div>
              <div className={classes.datePickerContainer}>
                <DatePickerV2
                  formik={
                    formikProps as unknown as FormikProps<
                      Record<string, unknown>
                    >
                  }
                  format={labraUIDateFormat}
                  field={{
                    id: 'toDate',
                    title: 'End date',
                    humanReadableKey: 'End date',
                    placeHolder: 'End date',
                    isRequired: true,
                    maxDate: 'today',
                  }}
                  views={['year', 'month', 'day']}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
